import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import calendarEventTypeService from '../services/calendar-event-type.service';
import type {
  CalendarEventType,
  CalendarEventTypeCreateInput,
  CalendarEventTypeUpdateInput,
} from '../types/calendar-event-type';

interface CalendarEventTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  calendarEventType?: CalendarEventType | null;
  onSuccess: () => void;
}

export function CalendarEventTypeFormDialog({
  open,
  onClose,
  mode,
  calendarEventType,
  onSuccess,
}: CalendarEventTypeFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or calendarEventType changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && calendarEventType) {
        setName(calendarEventType.name);
      } else {
        setName('');
      }
      setError(null);
    }
  }, [open, mode, calendarEventType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: CalendarEventTypeCreateInput = {
          name: name.trim(),
        };
        await calendarEventTypeService.createCalendarEventType(currentWorkspace.id, token, data);
      } else if (calendarEventType) {
        const data: CalendarEventTypeUpdateInput = {
          name: name.trim(),
        };
        await calendarEventTypeService.updateCalendarEventType(currentWorkspace.id, calendarEventType.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} calendar event type:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('eventType.form.createTitle') : t('eventType.form.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('eventType.form.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('eventType.form.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('common.creating') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('eventType.form.createButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
